/**
 * §11.2 — the sync failure banner.
 *
 * The problem it solves: the sync runs unattended at 6am. If it stops, nothing
 * says so. Books that silently stop updating look exactly like books with no
 * new transactions, and the operator finds out at tax time.
 *
 * Two DIFFERENT problems, deliberately worded differently rather than merged
 * into one "sync problem" message:
 *
 *   stale       the data is old. Annoying, recoverable by running a sync.
 *   tokenAtRisk the CREDENTIAL is approaching deletion. Mercury destroys a
 *               token unused for 45 days, and recovery is manual — a person
 *               logging into Mercury and issuing a new one. Ignoring this
 *               costs an afternoon; ignoring stale data costs a morning.
 *
 * Polls every five minutes. This is a background signal, not a live monitor,
 * and a tighter interval would be requests spent to tell the operator nothing.
 */

import { useEffect, useState } from 'react';
import { api, type BankSyncStatus, type SyncStatus } from '../api.js';
import { onSyncChanged } from '../lib/sync-signal.js';

const POLL_MS = 5 * 60 * 1000;

export function SyncBanner() {
  const [status, setStatus] = useState<SyncStatus | null>(null);

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      try {
        const next = await api.syncStatus();
        if (!cancelled) setStatus(next);
      } catch {
        // Silent. A failed status check is not itself worth a banner — the
        // shell already surfaces a lost session, and a banner that shouted
        // about its own fetch would cry wolf during every API restart.
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), POLL_MS);
    // Read again the moment a sync runs from the Sync screen (review SN-1).
    const stop = onSyncChanged(() => void poll());
    return () => {
      cancelled = true;
      clearInterval(timer);
      stop();
    };
  }, []);

  if (!status) return null;

  /*
   * No bank connected is the expected local state and says nothing useful in
   * production either — the deploy log already covers it. (Before 0046 this
   * read `status.configured`, which asked whether the DEPLOYMENT had a token.)
   *
   * **BUT IT MUST NOT SWALLOW THE SCHEDULE WARNING, WHICH IS WHAT IT USED TO
   * DO.** This returned `null` BEFORE the `scheduleRegistered` check below, so
   * a business with zero live connections was the one state the banner could
   * not report — and for a business that HAD a feed, zero live connections is
   * what 0046's header calls "0040's trap". The reason given for the early
   * return, that the deploy log covers it, is a log nobody reads standing in
   * for the screen built so nobody has to. Reported as F-8.
   */
  const live = status.banks.filter((bank) => !bank.disabled);

  /*
   * NEVER CONNECTED and ALL DISABLED are different states, and the early
   * return used to treat them as one.
   *
   * A business with no connections at all has nothing to be warned about: that
   * is a fresh install, and the local case the old comment meant. A business
   * that HAS connections and none of them live is the opposite — it had a feed
   * and does not any more. Returning `null` there left the screen silent about
   * the one state that needs saying, and left the `scheduleRegistered` check
   * below unreachable for it too.
   */
  if (status.banks.length === 0 && status.scheduleRegistered) return null;

  const banners: Array<{ tone: 'urgent' | 'warn'; text: string }> = [];

  if (status.banks.length > 0 && live.length === 0) {
    banners.push({
      tone: 'urgent',
      text: 'No bank connection is active. Transactions are not reaching the books.',
    });
  }

  /*
   * EVERY BANK GETS ITS OWN BANNERS, AND EACH ONE SAYS WHICH BANK.
   *
   * One line per problem per connection, rather than a single worst-case
   * summary: a healthy bank must not hide an unhealthy one, which is the whole
   * reason the status is per connection. The name is only included when there
   * is more than one, because "Mercury: the last sync failed" reads oddly to
   * somebody who has exactly one bank and knows it.
   */
  const label = (bank: BankSyncStatus): string => (live.length > 1 ? `${bank.bank}: ` : '');

  for (const bank of live) {
    // A concrete failure outranks a vague one. Without this, a rejected token
    // produced BOTH "the last sync failed: 401 token rejected" and "no sync has
    // ever succeeded" — the same news twice, with the useless version on top.
    const hasFailureDetail = bank.lastRunStatus === 'failed';

    /*
     * NOTHING MAPPED, OR NOT RUN YET: SAY WHICH, AND WHAT TO DO (issue #54).
     *
     * A connection added an hour ago has never run, so it was told "no sync has
     * ever succeeded. Transactions are not reaching the books" -- true, and it
     * read as a fault with no reason and no next step. The operator reported
     * exactly that for Quickity Closers, whose connection had three accounts
     * mapped and was simply waiting for the 06:00 tick. These say what is
     * actually the case and where to act, and stand in for the vaguer lines
     * below rather than adding to them.
     */
    if (bank.mappedAccounts === 0) {
      banners.push({
        tone: 'warn',
        text: `${label(bank)}no Mercury account is mapped, so nothing is imported. Map them in Settings, Bank Connections.`,
      });
      continue;
    }
    if (bank.lastRunAt === null) {
      // Only promise the schedule when it is running (review SN-2): beside "the
      // scheduled sync did not start", a time it runs would be a contradiction.
      banners.push({
        tone: 'warn',
        text: status.scheduleRegistered
          ? `${label(bank)}not synced yet. The schedule runs it ${status.scheduleDescription}; Sync Now on the Sync screen runs it now.`
          : `${label(bank)}not synced yet. The scheduled sync is not running, so Sync Now on the Sync screen is the only way it runs.`,
      });
      continue;
    }

    // Token first: it is the only condition here that becomes unrecoverable.
    if (bank.tokenUrgent) {
      banners.push({
        tone: 'urgent',
        text:
          `${label(bank)}the Mercury token will be deleted in ${bank.daysUntilTokenDeleted} day(s) of continued inactivity. ` +
          'Once deleted it cannot be recovered — a new one has to be issued from Mercury by hand.' +
          (bank.tokenCountdownFrom === 'first_attempt'
            ? ' No sync has ever succeeded, so this counts from the first attempt and may be generous.'
            : ''),
      });
    } else if (bank.tokenAtRisk) {
      banners.push({
        tone: 'warn',
        text:
          `${label(bank)}no successful sync for ${Math.floor((bank.hoursSinceSuccess ?? 0) / 24)} day(s). ` +
          `Mercury deletes tokens unused for 45 days; this one has ${bank.daysUntilTokenDeleted} day(s) left.`,
      });
    }

    if (hasFailureDetail) {
      banners.push({
        tone: 'warn',
        text: `${label(bank)}the last sync failed${bank.lastRunError ? `: ${bank.lastRunError}` : '.'}`,
      });
    } else if (bank.stale) {
      // Only when nothing more specific is available to say.
      banners.push({
        tone: 'warn',
        text: bank.lastSuccessAt
          ? `${label(bank)}last successful sync was ${formatWhen(bank.lastSuccessAt)}. Transactions since then are not in the books.`
          : `${label(bank)}no sync has ever succeeded. Transactions are not reaching the books.`,
      });
    }
  }

  if (!status.scheduleRegistered) {
    // scheduleRegistered, not scheduleEnabled: without a token the schedule
    // never starts, and the old field reported the intent rather than the
    // outcome — "on" while nothing ran.
    banners.push({
      tone: 'warn',
      text: status.scheduleEnabled
        ? 'The scheduled sync did not start. Transactions arrive only when a sync is run by hand.'
        : 'The scheduled sync is turned off. Transactions arrive only when a sync is run by hand.',
    });
  }

  // Sandbox data must be impossible to mistake for the real books, and since
  // 0046 one business may legitimately have one bank in each.
  for (const bank of live.filter((candidate) => candidate.environment !== 'production')) {
    banners.push({
      tone: 'warn',
      text: `${label(bank)}connected to Mercury ${bank.environment}. These are not your real transactions.`,
    });
  }

  if (banners.length === 0) return null;

  return (
    <div className="banners">
      {banners.map((banner) => (
        <div key={banner.text} className={`banner banner-${banner.tone}`} role="status">
          {/* Inner wrapper so a long Mercury error wraps inside a readable
              measure instead of running to the window edge. */}
          <p className="banner-text">{banner.text}</p>
        </div>
      ))}
    </div>
  );
}

function formatWhen(iso: string): string {
  const hours = (Date.now() - new Date(iso).getTime()) / 36e5;
  if (hours < 1) return 'less than an hour ago';
  if (hours < 48) return `${Math.floor(hours)} hours ago`;
  return `${Math.floor(hours / 24)} days ago`;
}
